import type {
    ComposableOptionsBase,
    SavedPaymentMethod,
    SiteMembershipContext
} from '~/types';
import type {
    PaginatedRequest,
    PaginatedPayload
} from 'x1-base/types';
import {
    useApiCommon,
    useCustomFetch,
    type UseCustomFetchOptions
} from '~/composables';
import { SavedPaymentMethodModel } from '~/models';
import type { NitroFetchOptions } from 'nitropack';
import { isError as lo_isError } from 'es-toolkit';
import { useApiUtils } from 'x1-base/composables';

export interface UsePaymentMethodsV2Options extends ComposableOptionsBase {
    siteMembershipContext?: SiteMembershipContext;
}
export function usePaymentMethodsV2(options: Partial<UsePaymentMethodsV2Options> = {}) {
    const apiBasePath = `/v1/payment-methods`;
    const useApiUtilsObj = useApiUtils();
    const useApiCommonObj = useApiCommon();
    const $_fetch = useCustomFetch();
    const genericCrudMethods = useGenericCrudMethods<SavedPaymentMethod>(
        apiBasePath,
        SavedPaymentMethodModel,
        void(0)
    );


    // TODO: This needs to be converted into proper pagination on the backend
    async function getItems(args: PaginatedRequest): Promise<PaginatedPayload<SavedPaymentMethodModel> | Error> {
        const response = await getSavedPaymentMethods();

        if (lo_isError(response)) {
            return response;
        }

        const data = response.map<SavedPaymentMethodModel>((obj) => SavedPaymentMethodModel.toPlainObject<SavedPaymentMethodModel>(obj));

        return {
            pagination: useApiCommonObj.getSinglePagePaginationObj(data.length),
            data
        };
    }

    async function getSavedPaymentMethods(): Promise<SavedPaymentMethod[] | Error> {
        const url = `${apiBasePath}/?format=json`;
        const config: NitroFetchOptions<string> = {
            method: 'GET'
        };

        try {
            return await $_fetch<SavedPaymentMethod[]>(url, config);
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function setDefault(id: string): Promise<SavedPaymentMethod[] | Error> {
        return await setOption('default', id);
    }

    async function setBackup(id: string): Promise<SavedPaymentMethod[] | Error> {
        return await setOption('backup', id);
    }

    async function setOption(type: 'default' | 'backup', id: string): Promise<SavedPaymentMethod[] | Error> {
        const url = `${apiBasePath}/${id}/set-as-${type}/`;
        const config: NitroFetchOptions<string> = {
            method: 'POST'
        };

        try {
            return await $_fetch<SavedPaymentMethod[]>(url, config);
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    return {
        ...genericCrudMethods,
        getItems,
        getSavedPaymentMethods,
        setDefault,
        setBackup
    };
}
